import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useCompanyContext } from './useCompanyContext';
import { useChaveDoContexto } from '../stores/chaveDoContexto';

interface CompanySelectionState {
  id: string;
  name: string;
}

interface UseCompanySelectionOptions {
  isEditMode: boolean;
  currentCompanyId?: string;
}

interface UseCompanySelectionResult {
  modalOpen: boolean;
  selectedCompany: CompanySelectionState | null;
  showInfoBox: boolean;
  isGlobalSelection: boolean;
  openModal: () => void;
  handleSelect: (companyId: string, companyName: string) => void;
}

/**
 * A escolha é da tela **e do contexto**: a mesma tela, na clínica ao lado, alcança outras
 * empresas. Sem o contexto na chave, quem escolhia uma empresa e trocava de clínica
 * continuava com ela no formulário — o nome de uma empresa que o contexto novo não atende,
 * e o registro apontando para ela.
 */
function getStorageKey(contexto: string | null, pathname: string): string {
  return `companySelection:${contexto ?? 'sem-contexto'}:${pathname}`;
}

function readFromStorage(key: string): CompanySelectionState | null {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'id' in parsed &&
      'name' in parsed &&
      typeof (parsed as CompanySelectionState).id === 'string' &&
      typeof (parsed as CompanySelectionState).name === 'string'
    ) {
      return parsed as CompanySelectionState;
    }
    return null;
  } catch {
    return null;
  }
}

function writeToStorage(key: string, value: CompanySelectionState): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // QuotaExceededError or other storage errors — fail silently
  }
}

function removeFromStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Fail silently
  }
}

export function useCompanySelection(
  options: UseCompanySelectionOptions
): UseCompanySelectionResult {
  const { isEditMode } = options;
  const { shouldShowCompanyField, effectiveCompanyId, effectiveCompanyIdString } =
    useCompanyContext();
  const { pathname } = useLocation();
  const contexto = useChaveDoContexto();
  const storageKey = getStorageKey(contexto, pathname);
  // Determine initial state based on context
  const needsManualSelection = shouldShowCompanyField && !isEditMode;

  const [modalOpen, setModalOpen] = useState(() => {
    if (!needsManualSelection) return false;
    return !readFromStorage(storageKey);
  });
  const [manualSelection, setManualSelection] = useState<CompanySelectionState | null>(
    () => needsManualSelection ? readFromStorage(storageKey) : null
  );

  // Track that the component has fully mounted (StrictMode guard).
  // In React 18+ StrictMode, effects mount, unmount, then remount.
  // The mountedRef prevents cleanup from firing during the false unmount.
  const mountedRef = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      mountedRef.current = true;
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Cleanup sessionStorage on unmount (SPA navigation)
  useEffect(() => {
    return () => {
      if (mountedRef.current) {
        removeFromStorage(storageKey);
      }
    };
  }, [storageKey]);

  // A chave nova já esconde o que ficou gravado, mas a escolha em memória atravessaria a
  // troca sem isto: o formulário fica montado, e trocar de contexto não o remonta.
  const contextoAnterior = useRef(contexto);
  useEffect(() => {
    if (contextoAnterior.current === contexto) return;
    contextoAnterior.current = contexto;
    setManualSelection(null);
  }, [contexto]);

  // React to shouldShowCompanyField changes (header change mid-form)
  useEffect(() => {
    if (!shouldShowCompanyField) {
      // Header activated — clear manual selection
      setManualSelection(null);
      setModalOpen(false);
      removeFromStorage(storageKey);
    } else if (!isEditMode && !manualSelection && !readFromStorage(storageKey)) {
      // Header cleared — reopen modal if no selection exists
      setModalOpen(true);
    }
  }, [shouldShowCompanyField, isEditMode, storageKey, manualSelection]);

  const handleSelect = useCallback(
    (companyId: string, companyName: string) => {
      const selection: CompanySelectionState = { id: companyId, name: companyName };
      setManualSelection(selection);
      setModalOpen(false);
      writeToStorage(storageKey, selection);
    },
    [storageKey]
  );

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  // Determine what to return based on scenario
  if (isEditMode) {
    return {
      modalOpen: false,
      selectedCompany: null,
      showInfoBox: false,
      isGlobalSelection: false,
      openModal,
      handleSelect,
    };
  }

  if (!shouldShowCompanyField) {
    // Auto-filled by header or single binding
    return {
      modalOpen: false,
      selectedCompany: effectiveCompanyId
        ? { id: effectiveCompanyIdString, name: '' }
        : null,
      showInfoBox: false,
      isGlobalSelection: false,
      openModal,
      handleSelect,
    };
  }

  // Manual selection scenario
  return {
    modalOpen,
    selectedCompany: manualSelection,
    showInfoBox: !!manualSelection && !modalOpen,
    isGlobalSelection: manualSelection?.id === 'global',
    openModal,
    handleSelect,
  };
}
